import type {Department, Rec} from 'typings/portaro.be.types';
import type {GridCellEditRequest, RecordRow} from '../../../../../record-grid/lib/types';
import type {ToastMessageService} from 'shared/components/kp-toast-messages/toast-message.service';
import type {RecordGridDataService} from '../../../../../record-grid/services/record-grid.data-service';
import type RecordDataService from '../../../../../record/record.data-service';
import type WalkerService from 'shared/services/walker.service';
import type FinishedResponseInteractionService from 'shared/services/finished-response-interaction.service';
import type ModalConversationService from '../../../../../../modals/modal-conversation.service';
import {convertFieldTypeIdToFieldId} from 'shared/utils/record-field-utils';
import {
    SUTOR_REFERENCE_STATES, SUTOR_REPORT_STATES,
    SUTOR_REPORTS_PROCESSING_SETTINGS
} from '../../../../sutor-constants';

export class SutorReportsProcessingService {
    public static serviceName = 'sutorWorkReportProcessingService';

    /*@ngInject*/
    constructor(public readonly walker: WalkerService,
                public readonly currentDepartment: Department,
                private recordDataService: RecordDataService,
                private toastMessageService: ToastMessageService,
                private modalConversationService: ModalConversationService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private recordGridDataService: RecordGridDataService) {
    }

    public async getRefreshedReference(reference: Rec): Promise<Rec | null> {
        try {
            return await this.recordDataService.getById(reference.id);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return null;
        }
    }

    public async markReferenceAsInProgress(reference: Rec, reports: RecordRow[]): Promise<void> {
        try {
            await this.modalConversationService.confirmationDialog({text: 'Opravdu chcete označit průvodku stavem ve zpracování? Všechny výkazy na této průvodce budou označeny stavem ve zpracování.'});

            await this.recordGridDataService.editCells({
                cellEditRequests: [
                    {
                        record: reference.id,
                        fieldId: convertFieldTypeIdToFieldId(SUTOR_REPORTS_PROCESSING_SETTINGS.stateFieldId, [0, 0]),
                        setFieldValueRequest: {value: SUTOR_REFERENCE_STATES.IN_PROGRESS}
                    }
                ]
            });

            await this.markReportsWithState(reports, SUTOR_REPORT_STATES.IN_PROGRESS);

            this.toastMessageService.showSuccess('Stav úspěšně změněn na ve zpracování!');
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    public async markReferenceAsDone(reference: Rec, reports: RecordRow[]): Promise<void> {
        try {
            await this.modalConversationService.confirmationDialog({text: 'Opravdu chcete označit průvodku stavem hotovo? Všechny výkazy na této průvodce budou označeny stavem ke schválení.'});

            await this.recordGridDataService.editCells({
                cellEditRequests: [
                    {
                        record: reference.id,
                        fieldId: convertFieldTypeIdToFieldId(SUTOR_REPORTS_PROCESSING_SETTINGS.stateFieldId, [0, 0]),
                        setFieldValueRequest: {value: SUTOR_REFERENCE_STATES.DONE}
                    }
                ]
            });

            await this.markReportsWithState(reports, SUTOR_REPORT_STATES.TO_CONFIRM);

            this.toastMessageService.showSuccess('Stav úšpěšně změněn na hotový!');
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }

    private async markReportsWithState(reports: RecordRow[], state: string): Promise<void> {
        try {
            const cellEditRequests: GridCellEditRequest[] = [];

            reports.forEach((report) => {
                cellEditRequests.push({
                    record: report.id,
                    fieldId: convertFieldTypeIdToFieldId(SUTOR_REPORTS_PROCESSING_SETTINGS.reportStateFieldId, [0, 0]),
                    setFieldValueRequest: {value: state}
                });
            });

            await this.recordGridDataService.editCells({cellEditRequests});
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
        }
    }
}